import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import { MonitoringForm } from './components/MonitoringForm';
import { StatusDisplay } from './components/StatusDisplay';
import { BusResults } from './components/BusResults';
import { Login } from './components/Login';
import { Header } from './components/Header';
import { AdminPanel } from './components/AdminPanel';
import { AccessRequest } from './components/AccessRequest';
import { AccessRequestsPanel } from './components/AccessRequestsPanel';
import { MonitoringStatusPanel } from './components/MonitoringStatusPanel';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { busMonitoringAPI } from './services/api';
import { MonitoringConfig, StatusResponse, Bus } from './types';
import { Bus as BusIcon, Wifi, WifiOff } from 'lucide-react';

function MainApp() {
  const { isAuthenticated, isLoading: authLoading, isAdmin } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [buses, setBuses] = useState<Bus[]>([]);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [activeTab, setActiveTab] = useState<string>('monitoring');
  const [unauthorizedUserInfo, setUnauthorizedUserInfo] = useState<any>(null);

  // Check online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check for unauthorized user info from login error
  useEffect(() => {
    const storedUserInfo = localStorage.getItem('unauthorized_user_info');
    if (storedUserInfo) {
      try {
        setUnauthorizedUserInfo(JSON.parse(storedUserInfo));
      } catch (error) {
        console.error('Error parsing unauthorized user info:', error);
        localStorage.removeItem('unauthorized_user_info');
      }
    }
  }, []);

  // Fetch status periodically
  const fetchStatus = useCallback(async () => {
    try {
      const statusResponse = await busMonitoringAPI.getStatus();
      
      // Check if monitoring stopped unexpectedly
      if (status?.is_running && !statusResponse.is_running && statusResponse.in_memory_status) {
        toast.error('⚠️ Monitoring stopped unexpectedly. Thread may have crashed. You can restart monitoring if needed.');
        setBuses([]);
        setLastUpdated(null);
      }
      
      setStatus(statusResponse);
    } catch (error) {
      console.error('Failed to fetch status:', error);
      // If API is unreachable, don't clear the status immediately
      // This prevents UI flickering when there are temporary network issues
    }
  }, [status?.is_running]);

  // Fetch filtered buses
  const fetchBuses = useCallback(async () => {
    try {
      const busesResponse = await busMonitoringAPI.getFilteredBuses();
      if (busesResponse.status === 'success') {
        setBuses(busesResponse.buses);
        setLastUpdated(busesResponse.last_updated);
      } else {
        setBuses([]);
        setLastUpdated(null);
      }
    } catch (error) {
      console.error('Failed to fetch buses:', error);
      setBuses([]);
    }
  }, []);

  // Auto-refresh when monitoring is active
  useEffect(() => {
    if (!isAuthenticated) return;
    
    fetchStatus();
    fetchBuses();

    if (status?.is_running) {
      const interval = setInterval(() => {
        fetchStatus();
        fetchBuses();
      }, 10000); // Check every 10 seconds

      return () => clearInterval(interval);
    }
  }, [status?.is_running, fetchStatus, fetchBuses, isAuthenticated]);

  // Show loading screen while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-blue-600 p-3 rounded-2xl">
              <BusIcon className="h-12 w-12 text-white animate-pulse" />
            </div>
          </div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show access request page for unauthorized users
  if (!isAuthenticated && unauthorizedUserInfo) {
    return <AccessRequest userInfo={unauthorizedUserInfo} />;
  }

  // Show login if not authenticated and no unauthorized user info
  if (!isAuthenticated) {
    return <Login onUnauthorizedUser={setUnauthorizedUserInfo} />;
  }

  const handleStartMonitoring = async (config: MonitoringConfig) => {
    setIsLoading(true);
    try {
      const response = await busMonitoringAPI.startSearch(config);
      if (response.status === 'success') {
        toast.success('🚌 Bus monitoring started successfully!');
        await fetchStatus();
      } else {
        toast.error(response.message || 'Failed to start monitoring');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to start monitoring');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopMonitoring = async () => {
    setIsLoading(true);
    try {
      const response = await busMonitoringAPI.stopSearch();
      if (response.status === 'success') {
        toast.success('🛑 Bus monitoring stopped');
        setStatus(null);
        setBuses([]);
        setLastUpdated(null);
      } else {
        toast.error(response.message || 'Failed to stop monitoring');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to stop monitoring');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshBuses = async () => {
    setIsLoading(true);
    try {
      await fetchBuses();
      toast.success('📊 Bus data refreshed');
    } catch (error) {
      toast.error('Failed to refresh bus data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const renderContent = () => {
    if (activeTab === 'admin' && isAdmin) {
      return <AdminPanel />;
    }
    
    if (activeTab === 'requests' && isAdmin) {
      return <AccessRequestsPanel />;
    }

    if (activeTab === 'monitoring-status' && isAdmin) {
      return <MonitoringStatusPanel />;
    }

    // Default monitoring content
    return (
        <div className="space-y-6">
          {/* Monitoring Form */}
          <MonitoringForm
            onStartMonitoring={handleStartMonitoring}
            isLoading={isLoading}
            isMonitoring={status?.is_running || false}
          />

          {/* Status Display */}
          {status?.is_running && (
            <StatusDisplay
              status={status}
              onStopMonitoring={handleStopMonitoring}
              isLoading={isLoading}
            />
          )}

          {/* Bus Results */}
          {(status?.is_running || buses.length > 0) && (
            <BusResults
              buses={buses}
              lastUpdated={lastUpdated}
              onRefresh={handleRefreshBuses}
              isLoading={isLoading}
            />
          )}

          {/* Empty State for First Time Users */}
          {!status?.is_running && buses.length === 0 && (
            <div className="text-center py-12">
              <div className="bg-gradient-to-r from-blue-100 to-purple-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <BusIcon className="w-10 h-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Welcome to TNSTC Bus Monitor
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Start monitoring bus availability by filling out the form above. 
                We'll continuously check for seat availability and notify you when buses match your criteria.
              </p>
            </div>
          )}
        </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Toaster position="top-right" />
      
      {/* Header */}
      <Header 
        activeTab={activeTab} 
        onTabChange={isAdmin ? handleTabChange : undefined} 
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">  
        {renderContent()}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-gray-500">
            <p>TNSTC Bus Monitoring System - Real-time bus availability tracking</p>
            <p className="mt-1">Make sure your Flask API server is running on port 8000</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
}

export default App; 